import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Check, Headphones, Inbox, Loader2, Music, Sparkles, X } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db, ensureAnonymousAuth } from '../../services/firebase';
import { matchedLinkFields, subscribeToRequestsByEventId, type SongRequest } from '../../services/requests';
import {
  decideAsCoApprovalDj,
  getCoApprovalDjSuggestion,
  resolveSpotifyLinkAsCoApprovalDj,
  setCoApprovalDjAlerts,
  setCoApprovalDjName,
  setSongVerdictAsCoApprovalDj,
  validateCoApprovalDjLink,
  type CoApprovalDjMatch,
} from '../../services/coApprovalDj';
import { analyzeSong, reanalyzeSongWithLyrics } from '../../services/songAnalysis';
import { needsLinkMatch, playModeConfig, type PlayMode } from '../../services/playModes';
import type { LibrarySongInput, LinkProvider, SaveSongLinkEntry } from '../../services/songMatchMemory';
import type { SongSearchResult } from '../../services/itunesApi';
import SongMatchModal, { type DurationVerified } from '../../components/dj/SongMatchModal';
import { SongAnalysisModal, type SongVerdict } from '../../components/dj/SongAnalysisModal';
import { ReviewHoldBanner } from '../../components/dj/ReviewHoldBanner';
import { useReviewHold } from '../../hooks/useReviewHold';
import { isAlreadyHandledError, isLinkClosedError, shareLinkErrorMessage } from '../../utils/shareLinks';
import { holderLabel, isHeldByOther, isOnCoApprovalDjList } from '../../utils/reviewHold';
import { attendeeDisplayName } from '../../utils/attendeeDisplayName';
import { formatElapsed } from '../../utils/dateUtils';
import { isrcForMatchedSong } from '../../utils/songIdentity';
import { advanceMatchRun, startMatchRun, type MatchRun } from '../../utils/matchRun';

const LINK_NAME = 'co-approval DJ';
const MATCH_SONG_KEYS = [
  'title', 'artist', 'albumArtUrl', 'durationMs', 'explicitFlag', 'itunesTrackId', 'isrc', 'previewUrl', 'catalogIds', 'source',
] as const;

interface EventInfo {
  name: string;
  djId: string;
}

// Same song update QueuePage's approveWithPlatformLink builds, sent to the server as a whitelisted match
function buildMatch(
  request: SongRequest,
  platformLink: string,
  platformName: string,
  songLinkUrl: string,
  itunesResult: SongSearchResult | null,
  durationOverrideMs: number | null,
  durationVerified: DurationVerified,
): CoApprovalDjMatch {
  let song: SongRequest['song'] = request.song;
  if (itunesResult) {
    const isRealItunes = itunesResult.trackId > 0;
    song = {
      ...request.song,
      title: itunesResult.trackName || request.song.title,
      artist: itunesResult.artistName || request.song.artist,
      albumArtUrl: itunesResult.artworkUrl100 || request.song.albumArtUrl || '',
      durationMs: (durationOverrideMs ?? (isRealItunes ? itunesResult.trackTimeMillis : 0)) || request.song.durationMs || 0,
      explicitFlag: isRealItunes ? itunesResult.trackExplicitness === 'explicit' : (request.song.explicitFlag || false),
      ...(itunesResult.trackId
        ? { itunesTrackId: String(itunesResult.trackId), isrc: isrcForMatchedSong(itunesResult, request.song) }
        : {}),
      previewUrl: isRealItunes ? (itunesResult.previewUrl || request.song.previewUrl) : request.song.previewUrl,
    };
  } else if (durationOverrideMs) {
    song = { ...request.song, durationMs: durationOverrideMs };
  }
  const linked = matchedLinkFields({ platformLink, platformName, songLinkUrl }, song);
  const songFields = Object.fromEntries(
    MATCH_SONG_KEYS
      .map(key => [key, (linked.song as unknown as Record<string, unknown>)[key]] as const)
      .filter(([, value]) => value !== undefined),
  );
  return {
    platformLink: linked.platformLink,
    platformName: linked.platformName,
    songLinkUrl: linked.songLinkUrl ?? null,
    ...(linked.spotifyUri ? { spotifyUri: linked.spotifyUri } : {}),
    durationVerified,
    matchVerified: true,
    entrySource: 'pending-approved',
    song: songFields,
  };
}

export default function CoApprovalDjPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventInfo, setEventInfo] = useState<EventInfo | null>(null);
  const [playMode, setPlayMode] = useState<PlayMode>('spotify_manual');
  const [coApprovalEnabled, setCoApprovalEnabled] = useState(false);
  const [helperName, setHelperName] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [hasEmail, setHasEmail] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [alertsSaving, setAlertsSaving] = useState(false);

  const [allRequests, setAllRequests] = useState<SongRequest[]>([]);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectingRequest, setRejectingRequest] = useState<SongRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [analysisSong, setAnalysisSong] = useState<{ title: string; artist: string } | null>(null);
  const [confidenceThreshold, setConfidenceThreshold] = useState<number | undefined>(undefined);

  const [matchingRequest, setMatchingRequest] = useState<SongRequest | null>(null);
  const [matchIndex, setMatchIndex] = useState(0);
  const [matchTotal, setMatchTotal] = useState(0);
  const [matchRun, setMatchRun] = useState<MatchRun | null>(null);
  const libraryEntryRef = useRef<SaveSongLinkEntry | null>(null);
  const linkClosedRef = useRef(false);

  const mode = playModeConfig(playMode);
  const credential = useMemo(() => (eventId && token ? { eventId, token } : undefined), [eventId, token]);

  const closeMatchModal = useCallback(() => {
    setMatchingRequest(null);
    setMatchIndex(0);
    setMatchTotal(0);
    setMatchRun(null);
    libraryEntryRef.current = null;
  }, []);

  const hold = useReviewHold({
    eventId,
    token: token ?? undefined,
    enabled: Boolean(helperName),
    onExpired: () => {
      closeMatchModal();
      setRejectingRequest(null);
      toast('Your hold ran out — the song is back on the list', { icon: '⏱️' });
    },
  });

  useEffect(() => {
    if (!eventId || !token) {
      setError('Invalid co-approval DJ link');
      setLoading(false);
      return;
    }
    const connect = async () => {
      try {
        await ensureAnonymousAuth();
        const linkState = await validateCoApprovalDjLink(eventId, token);
        const eventDoc = await getDoc(doc(db, 'events', eventId));
        if (!eventDoc.exists() || eventDoc.data().deletedAt != null) {
          setError(`This ${LINK_NAME} link is no longer active`);
          return;
        }
        setEventInfo({ name: eventDoc.data().name || 'Event', djId: eventDoc.data().djId as string });
        setPlayMode(linkState.playMode);
        setCoApprovalEnabled(linkState.coApprovalEnabled);
        setHelperName(linkState.helperName);
        setHasEmail(linkState.hasEmail);
        setAlertsEnabled(linkState.alertsEnabled);
      } catch (connectError) {
        console.error('Co-approval DJ link validation failed', connectError);
        setError(shareLinkErrorMessage(connectError, LINK_NAME));
      } finally {
        setLoading(false);
      }
    };
    connect();
  }, [eventId, token]);

  useEffect(() => {
    if (!eventId || !eventInfo) return;
    return subscribeToRequestsByEventId(
      eventId,
      setAllRequests,
      subscribeError => console.error('Co-approval DJ request listener failed', subscribeError),
    );
  }, [eventId, eventInfo]);

  // Hold chips and wait times refresh while the page is open
  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), 5000);
    return () => clearInterval(interval);
  }, []);

  const waiting = useMemo(() => {
    if (!eventInfo) return [];
    return allRequests
      .filter(request => isOnCoApprovalDjList(request, coApprovalEnabled, eventInfo.djId))
      .sort((first, second) => first.createdAt.localeCompare(second.createdAt));
  }, [allRequests, coApprovalEnabled, eventInfo]);

  const heldByOther = useCallback(
    (request: SongRequest) => request.id !== hold.heldRequestId && isHeldByOther(request, 'coApprovalDj', nowMs, true),
    [hold.heldRequestId, nowMs],
  );

  // The DJ removed or regenerated the link: switch the whole page to the closed-link message
  const showLinkClosed = (closedError: unknown) => {
    linkClosedRef.current = true;
    closeMatchModal();
    setRejectingRequest(null);
    setError(shareLinkErrorMessage(closedError, LINK_NAME));
  };

  const handleCallableError = (callableError: unknown) => {
    console.error('Co-approval DJ action failed', callableError);
    if (isLinkClosedError(callableError)) {
      showLinkClosed(callableError);
      return;
    }
    toast.error(shareLinkErrorMessage(callableError, LINK_NAME));
    if (isAlreadyHandledError(callableError)) closeMatchModal();
  };

  const handleSaveName = async () => {
    if (!eventId || !token || !nameInput.trim()) return;
    setSavingName(true);
    try {
      setHelperName(await setCoApprovalDjName(eventId, token, nameInput.trim()));
    } catch (nameError) {
      handleCallableError(nameError);
    } finally {
      setSavingName(false);
    }
  };

  const handleToggleAlerts = async (enabled: boolean) => {
    if (!eventId || !token) return;
    const previous = alertsEnabled;
    setAlertsEnabled(enabled);
    setAlertsSaving(true);
    try {
      setAlertsEnabled(await setCoApprovalDjAlerts(eventId, token, enabled));
    } catch (alertsError) {
      setAlertsEnabled(previous);
      handleCallableError(alertsError);
    } finally {
      setAlertsSaving(false);
    }
  };

  // --- Review run ---

  const openForReview = async (request: SongRequest, index: number, total: number, run: MatchRun | null): Promise<boolean> => {
    const outcome = await hold.claim(request.id);
    if (outcome.status === 'held') {
      toast(`${holderLabel(outcome.heldBy)} is reviewing this one`, { icon: '👀' });
      return false;
    }
    if (outcome.status === 'error') {
      if (isLinkClosedError(outcome.error)) {
        showLinkClosed(outcome.error);
        return false;
      }
      toast.error("Couldn't reserve this song — the DJ may open it too");
    }
    libraryEntryRef.current = null;
    setMatchRun(run);
    setMatchingRequest(request);
    setMatchIndex(index);
    setMatchTotal(total);
    return true;
  };

  const advance = async (fromId: string, run: MatchRun | null) => {
    if (!run) {
      hold.release();
      closeMatchModal();
      return;
    }
    let step = advanceMatchRun(run, fromId, new Set(waiting.filter(request => request.id !== fromId).map(request => request.id)));
    while (step.nextId) {
      if (linkClosedRef.current) return;
      const next = waiting.find(request => request.id === step.nextId);
      if (next && !heldByOther(next) && await openForReview(next, step.position, step.run.total, step.run)) return;
      step = advanceMatchRun(step.run, step.nextId, new Set(waiting.map(request => request.id)));
    }
    hold.release();
    closeMatchModal();
    toast.success('All caught up');
  };

  const handleStartReviewing = async () => {
    const available = waiting.filter(request => !heldByOther(request));
    if (available.length === 0) {
      toast('Every waiting song is being reviewed by someone else right now');
      return;
    }
    const run = startMatchRun(available.map(request => request.id));
    if (!(await openForReview(available[0], 1, run.total, run)) && !linkClosedRef.current) await advance(available[0].id, run);
  };

  const decide = async (
    requestId: string,
    decision: 'approve' | 'reject' | 'defer',
    extra: { reason?: string; match?: CoApprovalDjMatch; libraryEntry?: SaveSongLinkEntry } = {},
  ): Promise<boolean> => {
    if (!eventId || !token) return false;
    setProcessingId(requestId);
    try {
      const { librarySaved } = await decideAsCoApprovalDj({ eventId, token, requestId, decision, ...extra });
      if (librarySaved === false) toast.error('Approved, but saving the link to the library failed');
      return true;
    } catch (decisionError) {
      handleCallableError(decisionError);
      return false;
    } finally {
      setProcessingId(null);
    }
  };

  const approvalToast = () => {
    toast.success(coApprovalEnabled ? 'Approved — sent to the co-approver' : 'Approved');
  };

  const handleModalApproveWithLink = async (
    requestId: string,
    platformLink: string,
    platformName: string,
    songLinkUrl: string,
    itunesResult: SongSearchResult | null,
    durationOverrideMs: number | null,
    durationVerified: DurationVerified,
  ) => {
    const request = waiting.find(candidate => candidate.id === requestId) ?? matchingRequest;
    if (!request) return;
    const match = buildMatch(request, platformLink, platformName, songLinkUrl, itunesResult, durationOverrideMs, durationVerified);
    const libraryEntry = libraryEntryRef.current ?? undefined;
    if (await decide(requestId, 'approve', { match, libraryEntry })) {
      toast.success(`Matched on ${platformName} — ${coApprovalEnabled ? 'sent to the co-approver' : 'approved'}`);
      await advance(requestId, matchRun);
    }
  };

  const handleModalApproveDirect = async (requestId: string) => {
    if (await decide(requestId, 'approve')) {
      approvalToast();
      await advance(requestId, matchRun);
    }
  };

  const handleModalReject = async (requestId: string, reason: string) => {
    if (await decide(requestId, 'reject', { reason })) {
      toast.success('Request rejected');
      await advance(requestId, matchRun);
    }
  };

  const handleModalDefer = async (requestId: string) => {
    if (await decide(requestId, 'defer')) {
      toast('Marked as "Researching" — come back to approve later', { icon: '🔍' });
      await advance(requestId, matchRun);
    }
  };

  const handleModalSkip = () => {
    if (matchingRequest) void advance(matchingRequest.id, matchRun);
  };

  const handleModalClose = () => {
    hold.release();
    closeMatchModal();
  };

  // --- Row actions ---

  const handleRowApprove = async (request: SongRequest) => {
    if (needsLinkMatch(request, mode)) {
      await openForReview(request, 0, 0, null);
      return;
    }
    if (await decide(request.id, 'approve')) approvalToast();
  };

  const handleRowReject = async (request: SongRequest) => {
    const outcome = await hold.claim(request.id);
    if (outcome.status === 'held') {
      toast(`${holderLabel(outcome.heldBy)} is reviewing this one`, { icon: '👀' });
      return;
    }
    if (outcome.status === 'error' && isLinkClosedError(outcome.error)) {
      showLinkClosed(outcome.error);
      return;
    }
    setRejectingRequest(request);
    setRejectionReason('');
  };

  const confirmRowReject = async () => {
    if (!rejectingRequest) return;
    if (await decide(rejectingRequest.id, 'reject', { reason: rejectionReason })) {
      toast.success('Request rejected');
      setRejectingRequest(null);
      setRejectionReason('');
    }
  };

  const cancelRowReject = () => {
    hold.release();
    setRejectingRequest(null);
  };

  // --- Overrides for SongMatchModal and the analysis modal ---

  const loadSuggestion = useCallback(async (song: LibrarySongInput, provider: LinkProvider) => {
    if (!eventId || !token) return null;
    const result = await getCoApprovalDjSuggestion(eventId, token, song, provider);
    setConfidenceThreshold(result.confidenceThreshold);
    return result.suggestion;
  }, [eventId, token]);

  const resolveShortLink = useCallback(async (url: string) => {
    if (!eventId || !token) throw new Error('This link is missing its token');
    return resolveSpotifyLinkAsCoApprovalDj(eventId, token, url);
  }, [eventId, token]);

  const storeLibraryEntry = useCallback((entry: SaveSongLinkEntry) => { libraryEntryRef.current = entry; }, []);

  const analyze = useCallback((title: string, artist: string) => analyzeSong(title, artist, credential), [credential]);
  const reanalyze = useCallback(
    (title: string, artist: string, lyrics: string) => reanalyzeSongWithLyrics(title, artist, lyrics, credential),
    [credential],
  );
  const setVerdict = useCallback(async (title: string, artist: string, verdict: SongVerdict | null) => {
    if (!eventId || !token) return;
    await setSongVerdictAsCoApprovalDj(eventId, token, title, artist, verdict);
  }, [eventId, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
          <p className="text-base-content/60">Connecting...</p>
        </div>
      </div>
    );
  }

  if (error || !eventInfo) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-12 h-12 text-warning mx-auto mb-3" />
          <h1 className="text-xl font-bold mb-2">Access Denied</h1>
          <p className="text-base-content/60">{error ?? `This ${LINK_NAME} link is no longer active`}</p>
        </div>
      </div>
    );
  }

  if (!helperName) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <Headphones className="w-10 h-10 text-primary mx-auto mb-3" />
          <h1 className="text-xl font-bold text-center mb-1">Co-approval DJ</h1>
          <p className="text-sm text-base-content/60 text-center mb-4">{eventInfo.name}</p>
          <label htmlFor="helper-name" className="block text-sm font-medium mb-1">
            Your name (the DJ will see it on each song you approve)
          </label>
          <input
            id="helper-name"
            value={nameInput}
            onChange={event => setNameInput(event.target.value)}
            maxLength={40}
            className="input input-bordered w-full mb-2"
            autoFocus
          />
          <p className="text-xs text-base-content/50 mb-3">You can't change it later.</p>
          <button
            onClick={handleSaveName}
            disabled={!nameInput.trim() || savingName}
            className="btn btn-primary w-full"
          >
            {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  const takesLinks = !mode.automatic;

  return (
    <div className="min-h-screen bg-base-100">
      <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border-b border-indigo-500/30 p-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <Headphones className="w-6 h-6 text-indigo-500 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-lg font-bold">Co-approval DJ</h1>
            <p className="text-sm text-base-content/60 truncate">{eventInfo.name} · {helperName}</p>
          </div>
          <span className="ml-auto text-sm font-medium text-indigo-500 flex-shrink-0">{waiting.length} waiting</span>
        </div>
        {hasEmail && (
          <label className="max-w-lg mx-auto mt-3 flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="toggle toggle-sm toggle-primary"
              checked={alertsEnabled}
              disabled={alertsSaving}
              onChange={event => handleToggleAlerts(event.target.checked)}
            />
            Alert emails: {alertsEnabled ? 'On' : 'Off'}
          </label>
        )}
        <p className="max-w-lg mx-auto mt-2 text-xs text-base-content/50">
          Your approval counts as the DJ's.{coApprovalEnabled ? ' Approved songs go to the co-approver next.' : ''}
        </p>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-3">
        {waiting.length > 0 && (
          <button onClick={handleStartReviewing} className="btn btn-primary w-full flex items-center justify-center gap-2">
            <Inbox className="w-4 h-4" />
            Start reviewing ({waiting.length})
          </button>
        )}

        {waiting.length === 0 ? (
          <div className="text-center py-12">
            <Music className="w-12 h-12 text-base-content/20 mx-auto mb-3" />
            <p className="text-base-content/60 font-medium">No songs waiting for review</p>
            <p className="text-sm text-base-content/40 mt-1">New requests appear here automatically.</p>
          </div>
        ) : (
          waiting.map(request => {
            const blocked = heldByOther(request);
            const busy = processingId === request.id;
            return (
              <div key={request.id} className="rounded-lg border bg-base-200/50 border-base-300 p-4">
                <div className="flex items-start gap-3">
                  {request.song.albumArtUrl ? (
                    <img src={request.song.albumArtUrl} alt="" className="w-12 h-12 rounded-md object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-md bg-base-300 flex items-center justify-center flex-shrink-0">
                      <Music className="w-5 h-5 text-base-content/30" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{request.song.title}</p>
                    <p className="text-sm text-base-content/60 truncate">{request.song.artist}</p>
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      {request.prizeSkipTheLine && (
                        <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded">🏆 Prize pick</span>
                      )}
                      {request.superRequest && (
                        <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded">👑 People's Pick</span>
                      )}
                      {request.isOverflow && (
                        <span className="text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 px-1.5 py-0.5 rounded">Overflow</span>
                      )}
                      {request.song.explicitFlag && (
                        <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded">Explicit</span>
                      )}
                    </div>
                    <p className="text-xs text-base-content/50 mt-1">
                      by {attendeeDisplayName(request.requestedBy.nickname, request.requestedBy.oderId)} · waiting {formatElapsed(request.createdAt)}
                    </p>
                    {request.djNotes && (
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">Note: {request.djNotes}</p>
                    )}
                    {blocked && (
                      <p className="text-xs font-medium text-indigo-600 dark:text-indigo-400 mt-1">
                        {holderLabel(request.reviewHold)} is reviewing
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setAnalysisSong({ title: request.song.title, artist: request.song.artist })}
                    className="p-2 rounded-lg text-purple-500 hover:bg-purple-500/10 flex-shrink-0"
                    title="AI Song Analysis"
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => handleRowApprove(request)}
                    disabled={blocked || busy}
                    className="btn btn-sm flex-1 bg-green-500 hover:bg-green-600 text-white border-none"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" />{takesLinks && needsLinkMatch(request, mode) ? 'Match' : 'Approve'}</>}
                  </button>
                  <button
                    onClick={() => handleRowReject(request)}
                    disabled={blocked || busy}
                    className="btn btn-sm flex-1 bg-red-500 hover:bg-red-600 text-white border-none"
                  >
                    <X className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {rejectingRequest && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !processingId && cancelRowReject()} />
          <div
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 max-w-sm mx-auto bg-base-100 rounded-xl p-5 shadow-xl"
            {...hold.activityHandlers}
          >
            <ReviewHoldBanner warning={hold.warning} onKeep={hold.renew} />
            <h3 className="text-lg font-bold mb-1">Reject Song</h3>
            <p className="text-sm text-base-content/60 mb-3">
              {rejectingRequest.song.title} — {rejectingRequest.song.artist}
            </p>
            <textarea
              value={rejectionReason}
              onChange={event => setRejectionReason(event.target.value)}
              placeholder="Reason for rejection (optional)..."
              className="textarea textarea-bordered w-full mb-3"
              rows={3}
              maxLength={500}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={confirmRowReject}
                disabled={!!processingId}
                className="btn btn-sm flex-1 bg-red-500 hover:bg-red-600 text-white border-none"
              >
                {processingId ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject'}
              </button>
              <button onClick={cancelRowReject} disabled={!!processingId} className="btn btn-sm btn-ghost flex-1">
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      <div {...hold.activityHandlers}>
        <SongMatchModal
          request={matchingRequest}
          eventName={eventInfo.name}
          batchIndex={matchIndex}
          batchTotal={matchTotal}
          playMode={playMode}
          djUid={eventInfo.djId}
          onApproveWithPlatformLink={handleModalApproveWithLink}
          onApproveDirect={handleModalApproveDirect}
          onReject={handleModalReject}
          onDefer={handleModalDefer}
          onSkip={handleModalSkip}
          onClose={handleModalClose}
          onAnalyzeSong={(title, artist) => setAnalysisSong({ title, artist })}
          loadSuggestion={loadSuggestion}
          confidenceThreshold={confidenceThreshold}
          resolveShortLink={resolveShortLink}
          saveLibraryEntry={storeLibraryEntry}
          holdBanner={<ReviewHoldBanner warning={hold.warning} onKeep={hold.renew} />}
        />
      </div>

      {analysisSong && (
        <SongAnalysisModal
          title={analysisSong.title}
          artist={analysisSong.artist}
          onClose={() => setAnalysisSong(null)}
          analyze={analyze}
          reanalyze={reanalyze}
          setVerdict={setVerdict}
        />
      )}
    </div>
  );
}
